import React, { useMemo } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';
import { useBoolean } from 'usehooks-ts';
import { formatEther } from 'viem';

import {
  IStakedDeposit,
  useRewardsOfOrThrow,
} from '@web/features/staking/hooks';
import { BoostModal } from '@web/features/staking/modals/BoostModal';
import { useCurrentCompletedUser } from '@web/features/user/service';
import { Button, showSnackbar, Tooltip, UnaIcon } from '@web/ui/components';
import { BoostIcon } from '@web/ui/icons';

const StakedDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.spacing('1000')};
`;

const RankAndReward = styled.div<{ disabled: boolean }>`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('1000')};

  background: ${({ theme, disabled }) =>
    disabled && disabled ? theme.color.background : 'none'};
  opacity: ${({ theme, disabled }) =>
    disabled ? theme.opacity.solid : theme.opacity.opaque};
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const RewardText = styled.p`
  color: ${({ theme }) => theme.color.onSurface};
  opacity: ${({ theme }) => theme.opacity.dense};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0750']};
`;

const Reward = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0250')};
`;

const UnaAmount = styled.p`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size[3]};
`;

const BoostedIndicator = styled.div`
  background: ${({ theme }) => theme.color.greenFixed};
  border-radius: ${({ theme }) => theme.shape.corner['0500']};
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ theme }) => theme.size[1500]};
  padding-inline: ${({ theme }) => theme.spacing('0500')};
  gap: ${({ theme }) => theme.spacing('0250')};
  margin-left: ${({ theme }) => theme.spacing('0250')};
  width: ${({ theme }) => theme.spacing('1500')};
  box-sizing: border-box;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: auto;
  }
`;

const BoostedText = styled.span`
  color: ${({ theme }) => theme.color.onGreenFixed};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['0750']};

  display: none;

  ${({ theme }) => theme.breakpoints.up('lg')} {
    display: flex;
  }
`;

const StakingText = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.color.onSurface};
  opacity: ${({ theme }) => theme.opacity.dense};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0750']};

  height: ${({ theme }) => theme.size['2500']};
`;

const ProcessingText = styled(StakingText)`
  justify-content: center;
`;

const PersonaShort = styled.p`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['0750']};
`;

const ActionContainer = styled.div``;

const timeStakedFor = (timestamp: number) => {
  const currentTime = new Date().getTime() / 1000;

  let difference = currentTime - timestamp;

  const days = Math.floor(difference / (24 * 3600));
  difference %= 24 * 3600;
  const hours = Math.floor(difference / 3600);
  difference %= 3600;
  const minutes = Math.floor(difference / 60);

  return `${days}D:${hours}H:${minutes}M`;
};

interface IStakedGridItemDetailsProps {
  persona: IPersonaFront;
  deposit: IStakedDeposit;
  disabled: boolean;
}

export const StakedGridItemDetails = ({
  persona,
  deposit,
  disabled,
}: IStakedGridItemDetailsProps) => {
  const t = useTranslations();
  const rewards = useRewardsOfOrThrow();
  const completedUser = useCurrentCompletedUser();

  const {
    value: isBoostModalOpen,
    setTrue: openBoostModal,
    setFalse: closeBoostModal,
  } = useBoolean(false);

  const handleBoostClick = () => {
    if (!completedUser) {
      return;
    }

    if (completedUser.user.boosts === 0) {
      showSnackbar(
        t('BULK_ACTION_BOOST_NOT_ENOUGH_BOOSTS', {
          boosts: 1,
        }),
      ),
        {
          severity: 'error',
        };
      return;
    }

    openBoostModal();
  };

  const rewardsAndBonus = useMemo(() => {
    const index = Number(persona.tokenId);

    const filterRewards =
      Number(formatEther(rewards[index]?.base || 0n)) +
      Number(formatEther(rewards[index]?.bonus || 0n));

    return filterRewards;
  }, [rewards, persona.tokenId]);

  const isBoosted = deposit.bonusPerDay > 0n;
  const isActionAccessible = !isBoosted && !disabled;

  return (
    <>
      <StakedDetailsContainer>
        <RankAndReward disabled={disabled}>
          <TopRow>
            <RewardText>{t('STAKING_GRID_ITEM_REWARD')}</RewardText>
            <PersonaShort>
              {t('PERSONA_SHORT')} #{persona.tokenId.toString()}
            </PersonaShort>
          </TopRow>
          <Reward>
            <UnaIcon size="1500" />
            <UnaAmount>{rewardsAndBonus.toFixed(2)}</UnaAmount>
            {isBoosted && (
              <Tooltip
                content={t('STAKING_GRID_ITEM_REWARD_TOOLTIP', {
                  boost: Number(formatEther(deposit.bonusPerDay)).toFixed(0),
                })}
                placement="top"
              >
                <BoostedIndicator>
                  <BoostIcon size="1000" color="onGreenFixed" />
                  <BoostedText>{t('STAKING_GRID_ITEM_BOOSTED')}</BoostedText>
                </BoostedIndicator>
              </Tooltip>
            )}
          </Reward>
        </RankAndReward>
        <ActionContainer>
          {disabled ? (
            <ProcessingText>
              {t('STAKING_GRID_ITEM_STAKING_PROCESSING_TEXT')}
            </ProcessingText>
          ) : (
            <StakingText className={isBoosted ? '' : 'hover-hide'}>
              {t('STAKING_GRID_ITEM_STAKING_FOR', {
                duration: timeStakedFor(deposit.depositDate),
              })}
            </StakingText>
          )}
          {isActionAccessible && (
            <Button
              className="hover-show"
              onClick={handleBoostClick}
              trailingIcon={<BoostIcon color="onYellow" />}
              label={t('STAKING_GRID_ITEM_BOOST')}
              fullWidth
            />
          )}
        </ActionContainer>
      </StakedDetailsContainer>

      <BoostModal
        personas={[persona]}
        open={isBoostModalOpen}
        onClose={closeBoostModal}
      />
    </>
  );
};
